/**
 * 지명 해석 단일 출처.
 *
 * 왜 통합했나: 같은 지명 데이터를 서로 다른 리졸버 셋이 각자 다른 사전·다른 순서로
 * 조회하고 있었다.
 *
 *   • SearchBar(keyword-parser)  → districts(58개) + CITIES        · 랜드마크 우선
 *   • 서버 검색 폴백(page.tsx)    → CITIES·COUNTRIES·SEARCH_PLACES·HOT_SPOTS·STATION_PLACES
 *   • 관련 가이드(related-guides) → CITIES·COUNTRIES 뿐
 *
 * 그래서 같은 "성수"가 검색바에선 상품명이고 서버에선 지명이었다(최근 30일 88회 검색
 * 전부 0건). 커버리지도 우선순위도 갈려 있어 한쪽을 고쳐도 다른 쪽이 따라오지 않는다.
 *
 * 매칭 규약: 정규화 후 **토큰 완전일치**. 부분 문자열 매칭이 아니므로 "성수동"처럼
 * 흔히 붙여 쓰는 형태는 사전에 별도 항목으로 있어야 한다(SEARCH_PLACES 규약과 동일).
 */

import {
  CITIES,
  COUNTRIES,
  HOT_SPOTS,
  DEFAULT_CITY_RADIUS_KM,
  type Bounds,
  type Country,
} from "@/config/locations";
import { ALL_DISTRICTS, ALL_LANDMARKS } from "@/config/districts";
import { SEARCH_PLACES, type SearchPlace } from "@/config/search-places";
import {
  JP_PREFECTURE_REGION_CODES,
  SEARCH_AREAS,
  TW_REGIONS,
  TW_REGION_SEARCH_LIVE,
  canonicalPlaceId,
  prefectureRegionSlug,
} from "@/config/search-places/areas";
import type { Locale } from "@/i18n/routing";
import { LANDMARKS as OSM_LANDMARKS } from "@/lib/mobility/landmarks";
import { insertScriptBoundarySpaces } from "@/lib/search/script-boundaries";

export type PlaceKind =
  | "country"
  | "city"
  | "district"
  | "landmark"
  | "spot"
  | "station";

export interface ResolvedPlace {
  id: string;
  kind: PlaceKind;
  /** 소속 도시 (CITIES의 id). 국가·도시 자신은 없음 */
  cityId?: string;
  /** kind === "country"일 때만 */
  country?: Country;
  /** 전 언어 표기 + 별칭 (정규화된 형태). 매칭과 스펠링 확장이 같은 배열을 쓴다 */
  names: string[];
  /**
   * 사람이 읽는 대표 표기 (원본 대소문자·공백 유지). names는 정규화되어 있어
   * 화면·슬러그에 쓸 수 없으므로 별도로 들고 다닌다.
   */
  label: string;
  /** 국가는 좌표로 좁히지 않는다(국가 전역 상품 경로를 탐) */
  center?: { lat: number; lng: number };
  /** 검색 박스 반폭(km). 도시=radius_km, 구/랜드마크=searchRadius, 그 외 기본값 */
  radiusKm?: number;
  /** 정사각형이 맞지 않는 넓은 지명(대만의 현)의 검색 박스 — 있으면 center±radiusKm 대신 쓴다 */
  bounds?: Bounds;
  /**
   * 지역(admin1) 검색으로 보낼 지명(일본 도도부현). 있으면 검색바가 좌표 박스 대신
   * `scope=region&region=<code>` 로 보낸다. center 는 서버 검색어 해석용으로 그대로 둔다.
   */
  region?: { code: string; slug: string; country: string };
  /** 로케일별 표시명 — 표기가 언어마다 다른 사전(섬 지명 등). 없으면 label */
  labels?: Partial<Record<Locale, string>>;
  /** 소속 나라(ISO) — 상위 도시가 없는 지명의 보조 문구용 */
  countryCode?: string;
  /**
   * 자동완성에만 나오고 resolvePlace(검색어 속 지명 해석)는 건너뛴다. 서비스 밖 나라는
   * "대만"을 골랐을 때 나라 검색으로 보내되, "중국 유심" 같은 상품 검색어를 나라로 빼앗지
   * 않게 한다.
   */
  suggestOnly?: boolean;
}

/**
 * COUNTRIES.name에 없는 구어 표기. Country.name은 로케일당 문자열 1개만 담을 수 있어
 * "대한민국"과 "한국"을 함께 넣을 수 없다 — 실제 검색에서 훨씬 흔한 축약형을 보완한다.
 */
const COUNTRY_ALIASES: Record<string, string[]> = {
  KR: ["한국", "korea", "south-korea", "southkorea", "rok"],
  JP: ["nippon", "nihon"],
  CN: ["중화인민공화국", "中國大陸", "中国大陆", "中華人民共和国"],
  TW: ["타이완", "臺灣", "タイワン", "中華民國", "中华民国", "formosa"],
  TH: ["타이", "타일랜드", "泰國"],
  VN: ["월남", "viet nam", "việt nam", "越南"],
  MY: ["말레이"],
  ID: ["인니"],
  AE: ["uae", "아랍 에미리트"],
  US: ["usa", "america", "united states of america", "아메리카", "미합중국", "美利堅", "美利坚"],
  GB: ["uk", "england", "britain", "great britain", "잉글랜드", "英格蘭", "英格兰"],
  DE: ["도이칠란트", "deutschland"],
  IT: ["이태리", "伊太利", "italia"],
  ES: ["españa", "espana", "에스파냐"],
};

/**
 * 국가 검색어 해석(resolvePlace)은 서비스 국가에만 열어둔다 — "중국 유심"이 나라 검색이 되면
 * 안 된다. 그 밖의 나라는 자동완성(suggestPlaces)에만 나온다(10-19, "대만" 무응답 수정).
 */
const SERVICE_COUNTRY_CODES = new Set(["KR", "JP"]);

/**
 * 도시와 이름이 같은 도시국가 — 나라 행을 따로 내면 "홍콩"이 두 줄이 된다. 도시가 대신한다.
 */
const CITY_STATE_CODES = new Set(["HK", "SG"]);

/**
 * CITIES.name 에 없는 도시 표기(정자체·섬 이름 등). 키 = CITIES id.
 */
const CITY_ALIASES: Record<string, string[]> = {
  "KR-JEJ": ["제주도", "제주섬", "jeju island", "jeju-do", "jejudo", "済州島", "济州岛", "濟州島", "濟州"],
  "JP-TSU": ["쓰시마", "tsushima island", "対馬島", "对马岛", "對馬島"],
  "TW-TPE": ["臺北", "타이페이", "taipei city", "台北市", "臺北市", "타이베이시", "타이페이시"],
  "TW-TXG": ["臺中", "台中市", "臺中市", "타이중시", "taichung city"],
  "TW-TNN": ["臺南", "台南市", "臺南市", "타이난시", "tainan city"],
  "TW-KHH": ["高雄市", "가오슝시", "카오슝", "kaohsiung city"],
  // 10-19 대만 도시 확장
  "TW-NWT": ["新北市", "신베이시", "new taipei city", "newtaipei", "반차오", "板橋", "板桥", "banqiao"],
  "TW-TAO": ["桃園市", "桃园市", "타오위안시", "taoyuan city"],
  "TW-TYA": [
    "타오위안 국제공항", "타오위안국제공항", "타오위엔공항", "taoyuan international airport", "tpe airport",
    "桃園國際機場", "桃园国际机场", "桃園国際空港", "台湾桃園国際空港",
  ],
  "TW-KEE": ["基隆市", "지룽시", "기륭", "keelung city", "jilong"],
  "TW-HSZ": ["新竹市", "신주시", "hsinchu city", "xinzhu"],
  "TW-YIL": ["宜蘭市", "宜兰市", "이란시", "ilan", "yilan city"],
  "TW-HUN": ["花蓮市", "花莲市", "화롄시", "화련", "hualien city", "hualian"],
  "TW-TTT": ["臺東", "台東市", "臺東市", "台东市", "타이둥시", "타이동", "taitung city"],
  "TW-KEN": [
    "컨띵", "kending", "kenting national park", "墾丁國家公園", "垦丁国家公园", "墾丁国立公園",
    "헝춘", "恆春", "恒春", "hengchun",
  ],
  "TW-CYI": ["嘉義市", "嘉义市", "자이시", "chiayi city", "jiayi"],
  "TW-SML": ["일월담", "日月潭國家風景區", "sunmoonlake", "sun-moon-lake", "르웨탄 호수"],
  // 10-19 area → 도시 승격(예전 area 별칭 그대로). 宮古(이와테현 미야코시)·石垣(돌담)처럼 다른
  // 뜻이 있는 짧은 표기는 넣지 않는다 — 자동완성은 앞부분 일치로 이미 찾는다.
  "JP-MMY": [
    "미야코", "미야코섬", "미야코 섬", "미야코지마시", "miyako", "miyako island", "miyako-jima",
    "miyakojima city", "宮古島市", "みやこじま", "宫古岛市", "이라부", "이라부섬", "伊良部島", "伊良部岛",
    "irabu", "시모지섬", "下地島",
  ],
  "JP-ISG": [
    "이시가키", "이시가키섬", "이시가키 섬", "이시가키시", "ishigaki island", "ishigakijima",
    "ishigaki-jima", "石垣市", "いしがきじま",
  ],
};

/**
 * 핫스팟·수기 지명·역은 도시보다 좁다. 여기서 시작하고 부족하면 호출자가 넓힌다.
 * 3.3km = 기존 page.tsx의 `HOT_SPOT_BOUNDS_OFFSET = 0.03`(도) 과 동일한 값 —
 * 위도 1도 ≈ 111km 환산. 통합하면서 검색 반경이 조용히 줄지 않게 맞춰둔다.
 */
const SPOT_RADIUS_KM = 3.3;

/** 구/군은 중간 반경, 랜드마크는 좁게 — 기존 keyword-parser의 값을 그대로 유지한다. */
const DISTRICT_RADIUS_KM = 5;
const LANDMARK_RADIUS_KM = 2;

/**
 * 표기 정규화.
 *
 * 소문자화 + **모든 공백 제거**가 핵심이다. 자동생성 역 사전이 원본 공공데이터의
 * 한자명을 그대로 실어 `"衿 井"`처럼 글자 사이에 공백이 낀 항목이 144건 있는데,
 * 토큰 완전일치 규약에서는 그런 항목이 영구 매칭 불가가 된다. 인덱스와 질의 양쪽을
 * 같은 규칙으로 접으면 생성기를 고치기 전에도 즉시 살아난다.
 */
const norm = (value: string) => value.toLocaleLowerCase().replace(/\s+/g, "");

/** name 객체(로케일별 표기)를 평탄화. "서울/Seoul"처럼 슬래시로 병기된 표기도 분해한다. */
function flattenName(name: Record<string, string | undefined>): string[] {
  const out: string[] = [];
  for (const value of Object.values(name)) {
    if (typeof value !== "string") continue;
    for (const part of value.split("/")) {
      const n = norm(part);
      if (n) out.push(n);
    }
  }
  return out;
}

function place(
  id: string,
  kind: PlaceKind,
  label: string,
  names: string[],
  extra: Omit<ResolvedPlace, "id" | "kind" | "names" | "label"> = {},
): ResolvedPlace {
  return { id, kind, label, names: [...new Set(names.filter(Boolean))], ...extra };
}

/** 대표 표기는 한국어 우선 — 사전 전반이 한국어를 기준 표기로 쓴다. */
const labelOf = (name: { ko?: string; en?: string }) => name.ko ?? name.en ?? "";

/**
 * 우선순위 = 배열 순서. 국가·도시가 먼저다.
 *
 * 클라이언트가 쓰던 "랜드마크 우선"은 버렸다. 더 구체적인 쪽을 앞세우면 도시명이
 * 동명의 구·랜드마크에 가려질 수 있는데, 도시는 검색 반경이 곧 정답이라 그 사고의
 * 대가가 훨씬 크다. 역(987개, 자동생성)은 오탐 여지가 가장 커서 항상 최후순위다.
 */
function buildIndex(): ResolvedPlace[] {
  const entries: ResolvedPlace[] = [];
  /**
   * 도시 이름과 표시명이 같은 좁은 지명(홈 핫스팟 "우붓"·구 "서귀포" 등)은 이름으로 찾지 않는다 —
   * 같은 이름이 두 줄로 나오고, 도시가 이미 그 이름을 받는다(10-19 여행지 확장). id 는 남겨
   * 예전 `?place=` 링크는 그대로 풀린다.
   */
  const cityNames = new Set(CITIES.flatMap((city) => flattenName(city.name)));
  const unlessCityName = (label: string, names: string[]) =>
    cityNames.has(norm(label)) ? [] : names;

  const countryEntry = (country: Country, suggestOnly: boolean) =>
    place(
      country.code,
      "country",
      labelOf(country.name),
      [...flattenName(country.name), ...(COUNTRY_ALIASES[country.code] ?? []).map(norm)],
      { country, ...(suggestOnly ? { suggestOnly } : {}) },
    );

  for (const country of COUNTRIES) {
    if (!SERVICE_COUNTRY_CODES.has(country.code)) continue;
    entries.push(countryEntry(country, false));
  }

  for (const city of CITIES) {
    if (!city.coordinates) continue;
    entries.push(
      place(
        city.id,
        "city",
        labelOf(city.name),
        [
          ...flattenName(city.name),
          ...(CITY_ALIASES[city.id] ?? []).map(norm),
          ...(destinationAliases[city.id] ?? []).map(norm),
        ],
        {
          center: { lat: city.coordinates.latitude, lng: city.coordinates.longitude },
          radiusKm: city.radius_km ?? DEFAULT_CITY_RADIUS_KM,
        },
      ),
    );
  }

  // 섬처럼 상위 도시가 없는 넓은 지명(10-19) — 도시 다음, 구·랜드마크보다 앞.
  for (const area of SEARCH_AREAS) {
    entries.push(
      place(
        area.id,
        "spot",
        area.name.ko,
        [...flattenName(area.name), ...area.aliases.map(norm)],
        {
          center: { lat: area.coordinates.latitude, lng: area.coordinates.longitude },
          radiusKm: area.radiusKm,
          ...(area.bounds ? { bounds: area.bounds } : {}),
          labels: area.name,
          countryCode: area.countryCode,
          ...(TW_REGION_SEARCH_LIVE && area.regionCode
            ? { region: { code: area.regionCode, slug: twRegionSlug(area.regionCode), country: area.countryCode } }
            : {}),
        },
      ),
    );
  }

  for (const district of ALL_DISTRICTS) {
    entries.push(
      place(
        district.id,
        "district",
        labelOf(district.name),
        unlessCityName(labelOf(district.name), [
          ...flattenName(district.name),
          ...(district.aliases ?? []).map(norm),
        ]),
        {
          cityId: district.cityId,
          center: {
            lat: district.coordinates.latitude,
            lng: district.coordinates.longitude,
          },
          radiusKm: DISTRICT_RADIUS_KM,
        },
      ),
    );
  }

  for (const landmark of ALL_LANDMARKS) {
    entries.push(
      place(
        landmark.id,
        "landmark",
        labelOf(landmark.name),
        [...flattenName(landmark.name), ...(landmark.aliases ?? []).map(norm)],
        {
          cityId: landmark.cityId,
          center: {
            lat: landmark.coordinates.latitude,
            lng: landmark.coordinates.longitude,
          },
          radiusKm: landmark.searchRadius ?? LANDMARK_RADIUS_KM,
        },
      ),
    );
  }

  // 수기 지명 사전은 검색 로그를 보고 늘려가는 데이터라 핫스팟보다 앞선다.
  for (const spot of SEARCH_PLACES) {
    const regionCode = JP_PREFECTURE_REGION_CODES[spot.id];
    entries.push(
      place(spot.id, "spot", spot.names[0] ?? "", unlessCityName(spot.names[0] ?? "", spot.names.map(norm)), {
        cityId: spot.cityId,
        center: { lat: spot.coordinates.latitude, lng: spot.coordinates.longitude },
        radiusKm: SPOT_RADIUS_KM,
        ...(regionCode
          ? {
              region: { code: regionCode, slug: prefectureRegionSlug(spot.id), country: "JP" },
              countryCode: "JP",
            }
          : {}),
      }),
    );
  }

  for (const spot of HOT_SPOTS) {
    if (!spot.coordinates) continue;
    entries.push(
      place(spot.id, "spot", labelOf(spot.name), unlessCityName(labelOf(spot.name), flattenName(spot.name)), {
        cityId: spot.cityId,
        center: { lat: spot.coordinates.latitude, lng: spot.coordinates.longitude },
        radiusKm: SPOT_RADIUS_KM,
      }),
    );
  }

  // OSM 명소(09-23, 지역·명소 필터와 같은 데이터) — 일본 4개 권역의 명소·역·동네·공항
  // 161곳, 5개 언어. 수기 사전·핫스팟에 없는 곳(USJ·기요미즈데라·이즈모 등)을 채운다.
  // 역은 "신주쿠 역"·"신주쿠역"처럼 띄어쓰기 두 형태를 모두 넣는다(토큰 완전일치 규약).
  for (const lm of OSM_LANDMARKS) {
    const names = new Set<string>();
    for (const value of Object.values(lm.name)) {
      if (!value) continue;
      names.add(norm(value));
      names.add(norm(value.replace(/\s+(역|駅|站|Station)$/i, "$1")));
    }
    for (const alias of OSM_LANDMARK_ALIASES[lm.name.ja] ?? []) names.add(norm(alias));
    entries.push(
      place(`osm:${lm.id}`, lm.kind === "station" ? "station" : "spot", lm.name.ko, [...names], {
        center: { lat: lm.lat, lng: lm.lng },
        radiusKm: SPOT_RADIUS_KM,
      }),
    );
  }

  // 자동생성 987개 — 오탐 여지가 가장 커서 항상 최후순위. 사전은 지연 로드라
  // 등록된 뒤에만 들어간다(registerStationPlaces · lib/search/station-places).
  for (const station of stationPlaces) {
    entries.push(
      place(station.id, "station", station.names[0] ?? "", station.names.map(norm), {
        cityId: station.cityId,
        center: {
          lat: station.coordinates.latitude,
          lng: station.coordinates.longitude,
        },
        radiusKm: SPOT_RADIUS_KM,
      }),
    );
  }

  // 서비스 밖 나라 — 자동완성 전용, 항상 최후순위. "인"을 치면 인천이 인도보다 먼저다.
  // 이름을 끝까지 치면(완전일치 등급) 맨 위로 온다.
  for (const country of COUNTRIES) {
    if (SERVICE_COUNTRY_CODES.has(country.code) || CITY_STATE_CODES.has(country.code)) continue;
    entries.push(countryEntry(country, true));
  }

  return entries;
}

/**
 * OSM 명소의 흔한 다른 이름(약칭·한자 독음 등). 키 = 일본어 이름(`landmarks-jp.json`).
 * 검색 로그에서 못 찾은 표기가 보이면 여기에 더한다.
 */
const OSM_LANDMARK_ALIASES: Record<string, string[]> = {
  "ユニバーサル・スタジオ・ジャパン": ["USJ", "유니버셜 스튜디오 재팬", "유니버설 스튜디오", "유니버셜 스튜디오", "유니버셜", "유니버설", "Universal Studios"],
  "清水寺": ["청수사", "기요미즈데라", "기요미즈 데라"],
  "東京スカイツリー": ["스카이트리", "Skytree"],
  "東京ディズニーランド": ["디즈니랜드", "도쿄 디즈니", "Disneyland"],
  "道頓堀": ["도톤보리"],
};

/** 표기 → 후보 목록(순위 오름차순). 같은 표기의 동명 지명을 전부 보존해 힌트로 고른다. */
type Indexed = { entry: ResolvedPlace; rank: number };

let byName: Map<string, Indexed[]> | null = null;
let byId: Map<string, ResolvedPlace> | null = null;

/**
 * 한국 역 사전(~161KB)은 공용 번들에서 빼려고 지연 등록한다 — 클라이언트는
 * `ensureStationPlaces()`(lib/search/station-places), 서버·검색 페이지는
 * `@/lib/search/station-places.eager` import. 등록되면 인덱스를 다시 만든다.
 */
let stationPlaces: readonly SearchPlace[] = [];

export function registerStationPlaces(places: readonly SearchPlace[]): void {
  if (stationPlaces === places) return;
  stationPlaces = places;
  byName = null;
  byId = null;
}

export function stationPlacesRegistered(): boolean {
  return stationPlaces.length > 0;
}

/**
 * 여행지의 다른 표기·하위 지명(config/search-places/destination-aliases.generated, 10-19) — 역
 * 사전과 같은 이유로 지연 등록한다(lib/search/station-places). 등록 전에도 여행지 이름(CITIES)
 * 자체는 찾는다.
 */
let destinationAliases: Readonly<Record<string, readonly string[]>> = {};

export function registerDestinationAliases(
  aliases: Readonly<Record<string, readonly string[]>>,
): void {
  if (destinationAliases === aliases) return;
  destinationAliases = aliases;
  byName = null;
  byId = null;
}

function ensureIndex(): { byName: Map<string, Indexed[]>; byId: Map<string, ResolvedPlace> } {
  if (byName && byId) return { byName, byId };
  const names = new Map<string, Indexed[]>();
  const ids = new Map<string, ResolvedPlace>();
  buildIndex().forEach((entry, rank) => {
    if (!ids.has(entry.id)) ids.set(entry.id, entry);
    for (const name of entry.names) {
      const list = names.get(name);
      if (list) list.push({ entry, rank });
      else names.set(name, [{ entry, rank }]);
    }
  });
  byName = names;
  byId = ids;
  return { byName, byId };
}

/**
 * 질의를 토큰으로 쪼갠다.
 *
 * 전체 문자열도 토큰으로 넣는 이유: "south korea"처럼 공백이 든 표기는 단어 단위로는
 * 절대 일치하지 않는다. 스크립트 경계 분리("聖水K-pop快閃" → "聖水 K-pop 快閃")는
 * related-guides가 이미 쓰는 방식을 그대로 재사용한다.
 */
function tokenize(query: string): string[] {
  const raw = [
    query,
    ...query.split(/\s+/),
    ...insertScriptBoundarySpaces(query).split(/\s+/),
  ];
  return [...new Set(raw.map(norm).filter(Boolean))];
}

export interface ResolveOptions {
  /**
   * 동명 지명 힌트. "중구"는 서울·부산에 다 있다 — 좌측 지역 칩이나 현재 URL의
   * 도시가 이 값이면 그 도시(또는 그 도시 자신)에 속한 후보를 먼저 고른다.
   */
  preferCityId?: string;
}

const belongsTo = (p: ResolvedPlace, cityId: string) => p.id === cityId || p.cityId === cityId;

/**
 * 질의에서 지명을 찾는다. 여러 토큰·여러 동명 후보가 걸리면
 * ① preferCityId 소속 후보 중 최고 순위 → ② 전체 최고 순위.
 */
export function resolvePlace(query: string, opts?: ResolveOptions): ResolvedPlace | null {
  if (!query.trim()) return null;
  const { byName: map } = ensureIndex();

  // 질의 **전체**가 한 지명과 일치하면 그것이 가장 구체적인 뜻이다 — "도쿄 스카이트리"가
  // 조각 토큰 "도쿄"(도시, 더 높은 순위)에 먹히지 않게 먼저 본다(09-23).
  const whole = map.get(norm(query))?.filter((hit) => !hit.entry.suggestOnly);
  if (whole && whole.length > 0) {
    const pick =
      (opts?.preferCityId &&
        whole.find((hit) => belongsTo(hit.entry, opts.preferCityId!))) ||
      whole.reduce((a, b) => (b.rank < a.rank ? b : a));
    return pick.entry;
  }

  let best: Indexed | null = null;
  let preferred: Indexed | null = null;
  for (const token of tokenize(query)) {
    const hits = map.get(token);
    if (!hits) continue;
    for (const hit of hits) {
      if (hit.entry.suggestOnly) continue;
      if (!best || hit.rank < best.rank) best = hit;
      if (opts?.preferCityId && belongsTo(hit.entry, opts.preferCityId)) {
        if (!preferred || hit.rank < preferred.rank) preferred = hit;
      }
    }
  }
  return (preferred ?? best)?.entry ?? null;
}

/**
 * id로 지명을 되찾는다(?place= 파라미터 복원용). 모르는 id는 null.
 * 도시로 올라간 예전 area id(area:JP-MIYAKOJIMA)는 그 도시로 풀린다.
 */
export function placeById(id: string): ResolvedPlace | null {
  if (!id) return null;
  return ensureIndex().byId.get(canonicalPlaceId(id)) ?? null;
}

const twRegionSlug = (code: string) => TW_REGIONS.find((r) => r.code === code)?.slug ?? code.toLowerCase();

/**
 * 지역(admin1) 코드 → 그 지역으로 보내는 지명(도도부현, 대만 직할시·현). 지역 검색 화면의
 * 위치 칸 표시용 — 대만은 도시·현 박스가 지역을 대신하므로 표(TW_REGIONS)로 찾는다.
 */
export function placeByRegionCode(code: string | undefined | null): ResolvedPlace | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  for (const entry of ensureIndex().byId.values()) {
    if (entry.region?.code === upper) return entry;
  }
  const tw = TW_REGIONS.find((r) => r.code === upper);
  return tw ? placeById(tw.placeId) : null;
}

export function expandPlaceSpellings(token: string): string[] {
  const hits = ensureIndex().byName.get(norm(token));
  return hits && hits.length > 0 ? [...hits[0].entry.names] : [];
}

/**
 * 부분 일치 자동완성(검색바 위치 칸, 09-23). resolvePlace 는 토큰 **완전일치**라 입력
 * 중인 "도쿄 스" 같은 조각을 못 받는다 — 여기서는 표기 앞부분 일치를 먼저, 그다음
 * 중간 일치를 인덱스 순위대로 돌려준다. 같은 지명의 여러 표기는 하나로 접는다.
 */
export function suggestPlaces(query: string, limit = 8): ResolvedPlace[] {
  const q = norm(query);
  if (!q) return [];
  const { byName: map } = ensureIndex();
  const best = new Map<string, { entry: ResolvedPlace; score: number }>();
  for (const [name, hits] of map) {
    const at = name.indexOf(q);
    if (at < 0) continue;
    // 완전일치 0 · 앞부분 1 · 중간 2 — 순위(rank)는 같은 등급 안에서만 비교한다.
    const grade = name === q ? 0 : at === 0 ? 1 : 2;
    for (const { entry, rank } of hits) {
      const score = grade * 100000 + rank;
      const prev = best.get(entry.id);
      if (!prev || score < prev.score) best.set(entry.id, { entry, score });
    }
  }
  return [...best.values()]
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((v) => v.entry);
}
